/** Sprint 5-d PR-4 · 輸入框（Enter 送出·Shift+Enter 換行）+ 語音輸入（§chat-voice-input）。 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, IconButton, TextField, Tooltip, Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';

import { useContinuousSpeech } from '../../../../hooks/useContinuousSpeech';
import { checkMicPermission } from '../../../../services/voiceService';
import type { VoiceMeta } from '../services/aiChatService';

interface Props {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  disabled?: boolean;
  busy?: boolean;      // §5.7 作業中：送出鈕改轉圈
  onVoiceMeta?: (m: VoiceMeta) => void;   // §2.6 語音訓練資料（input_channel/stt_text/stt_lang）
}

// i18n.language → Web Speech 辨識語言
const sttLangOf = (lang: string): string =>
  (lang === 'zh-CN' ? 'zh-CN' : lang === 'en' ? 'en-US' : 'zh-TW');

const MessageInput: React.FC<Props> = ({ value, onChange, onSend, disabled, busy, onVoiceMeta }) => {
  const { t, i18n } = useTranslation();
  const [composing, setComposing] = useState(false);   // 中文輸入法組字中不誤送
  const [denied, setDenied] = useState(false);

  const sttLang = sttLangOf(i18n.language);
  const { supported, recording, interim, start, stop } = useContinuousSpeech(sttLang);
  const sttAccum = useRef('');          // 本次語音累積的辨識原文（送出後 reset）
  const valueRef = useRef(value);
  valueRef.current = value;

  // 外部把輸入清空（送出 / 新對話 / 使用者刪光）→ 重置語音累積與 meta
  // 必修1：僅重置 sttAccum 不夠·須通知 ChatPage 把 voiceMeta 退回 text·
  // 否則清空後改打字送出會被記成語音（stt_text≠content 還會被當「修正過」的高價值樣本·汙染訓練資料）。
  useEffect(() => {
    if (value === '') {
      sttAccum.current = '';
      onVoiceMeta?.({ input_channel: 'text' });
    }
  }, [value, onVoiceMeta]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !composing) {
      e.preventDefault();
      if (!disabled && !busy && value.trim()) { if (recording) stop(); onSend(); }
    }
  };

  const onFinal = useCallback((text: string) => {
    if (!text) return;
    const cur = valueRef.current;
    const next = cur && !cur.endsWith(' ') ? `${cur} ${text}` : `${cur}${text}`;
    onChange(next);
    sttAccum.current = sttAccum.current ? `${sttAccum.current} ${text}` : text;
    onVoiceMeta?.({ input_channel: 'voice', stt_text: sttAccum.current, stt_lang: sttLang });
  }, [onChange, onVoiceMeta, sttLang]);

  const toggleMic = async () => {
    if (recording) { stop(); return; }
    const perm = await checkMicPermission();
    if (perm === 'denied' || perm === 'unsupported') { setDenied(true); return; }
    start(onFinal);
  };

  const handleSend = () => { if (recording) stop(); onSend(); };

  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-end', gap: 1, p: 2, borderTop: 1, borderColor: 'divider',
      pb: 'calc(16px + env(safe-area-inset-bottom, 0px))',   // RWD：不被手機底部操作列遮住
    }}>
      <Box sx={{ flex: 1 }}>
        {recording && (
          <Typography variant="caption" color="primary" sx={{ display: 'block', mb: 0.5 }}>
            {t('chat.voice.listening', '聆聽中…（再按一次結束）')}
            {interim && <Box component="span" sx={{ color: 'text.disabled' }}>　{interim}</Box>}
          </Typography>
        )}
        <TextField
          fullWidth multiline maxRows={6} size="small" value={value}
          placeholder={t('chat.inputPlaceholder', '輸入訊息（Enter 送出，Shift+Enter 換行）')}
          onChange={(e) => onChange(e.target.value)} onKeyDown={onKeyDown}
          onCompositionStart={() => setComposing(true)} onCompositionEnd={() => setComposing(false)}
          InputProps={{ sx: { fontSize: 16 } }}   // RWD：iOS 點輸入框不自動放大
        />
      </Box>
      {supported && (
        <Tooltip title={recording ? t('chat.voice.stop', '停止收音') : t('chat.voice.start', '語音輸入')}>
          <span>
            <IconButton color={recording ? 'primary' : 'default'} onClick={toggleMic} disabled={disabled || busy}
              sx={recording ? { animation: 'micPulse 1.2s ease-in-out infinite',
                '@keyframes micPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } } } : undefined}>
              {recording ? <StopIcon /> : <MicIcon />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      <IconButton color="primary" onClick={handleSend} disabled={disabled || busy || !value.trim()}>
        {busy ? <CircularProgress size={20} /> : <SendIcon />}
      </IconButton>

      {/* 權限引導 modal（對齊 VoicePage·非 toast·不裸用 Snackbar） */}
      <Dialog open={denied} onClose={() => setDenied(false)}>
        <DialogTitle>{t('chat.voice.deniedTitle', '無法使用麥克風')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('chat.voice.denied', '請在瀏覽器網址列的權限設定允許麥克風，再重試語音輸入。')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDenied(false)}>{t('chat.voice.deniedClose', '知道了')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessageInput;
